import React, { useState, useEffect } from 'react';
import { Card, Button, Input, Label } from '../ui';
import { useApp } from '../../contexts/AppContext';
import {
  getContactableUsers,
  sendBulkMessage,
  ContactableUser
} from '../../services/messagingService';
import { subscribeToCredits } from '../../services/creditService';
import { MessageChannel, MessagingCredits } from '../../types';

interface BulkMessageProps {
  showToast: (message: string, type: 'success' | 'error' | 'info') => void;
}

const CHANNEL_OPTIONS: { value: MessageChannel; label: string }[] = [
  { value: 'whatsapp', label: 'WhatsApp' },
  { value: 'sms', label: 'SMS' },
  { value: 'email', label: 'Email' }
];

const SMS_SEGMENT_LENGTH = 160;

const BulkMessage: React.FC<BulkMessageProps> = ({ showToast }) => {
  const { selectedOrganization } = useApp();
  const [channel, setChannel] = useState<MessageChannel>('whatsapp');
  const [recipients, setRecipients] = useState<ContactableUser[]>([]);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterType, setFilterType] = useState<'all' | 'player' | 'guardian'>('all');
  const [body, setBody] = useState('');
  const [subject, setSubject] = useState('');
  const [sending, setSending] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [credits, setCredits] = useState<MessagingCredits | null>(null);

  // Subscribe to credits
  useEffect(() => {
    if (!selectedOrganization?.id) return;
    const unsubscribe = subscribeToCredits(selectedOrganization.id, setCredits);
    return () => unsubscribe();
  }, [selectedOrganization?.id]);

  const availableCredits = credits?.messageCredits ?? 0;
  const insufficientCredits = selectedIds.size > availableCredits;

  // Load recipients when channel changes
  useEffect(() => {
    if (!selectedOrganization?.id) return;

    setLoading(true);
    setSelectedIds(new Set());
    getContactableUsers(selectedOrganization.id, channel)
      .then(setRecipients)
      .catch((err) => {
        console.error('Error loading recipients:', err);
        showToast('Failed to load recipients', 'error');
      })
      .finally(() => setLoading(false));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedOrganization?.id, channel]);

  const filteredRecipients = recipients.filter(r => {
    const contactField = channel === 'email' ? (r.email || '') : (r.phone || '');
    const matchesSearch = searchQuery === '' ||
      r.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      contactField.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesType = filterType === 'all' || r.type === filterType;
    return matchesSearch && matchesType;
  });

  const toggleSelect = (id: string) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const toggleSelectAll = () => {
    if (selectedIds.size === filteredRecipients.length) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(filteredRecipients.map(r => r.id)));
    }
  };

  const handleSend = async () => {
    if (!selectedOrganization?.id) return;
    if (selectedIds.size === 0 || !body.trim()) return;
    if (channel === 'email' && !subject.trim()) {
      showToast('Subject is required for email', 'error');
      return;
    }

    setSending(true);
    setShowConfirm(false);

    try {
      const result = await sendBulkMessage(
        selectedOrganization.id,
        channel,
        Array.from(selectedIds),
        body.trim(),
        channel === 'email' ? { subject: subject.trim() } : undefined
      );

      if (result.success) {
        showToast(`Bulk message sent to ${selectedIds.size} recipient(s)`, 'success');
        setBody('');
        setSubject('');
        setSelectedIds(new Set());
      } else {
        showToast(result.error || 'Failed to send bulk message', 'error');
      }
    } catch (err: any) {
      showToast(err.message || 'Failed to send bulk message', 'error');
    } finally {
      setSending(false);
    }
  };

  const getContactDisplay = (r: ContactableUser) => {
    return channel === 'email' ? r.email : r.phone;
  };

  const smsSegments = channel === 'sms' ? Math.ceil(body.length / SMS_SEGMENT_LENGTH) || 1 : 0;

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
      {/* Left: Recipients */}
      <Card>
        <div className="p-4 border-b border-secondary-200">
          <h3 className="font-semibold text-secondary-900 mb-3">Recipients</h3>

          {/* Channel selector */}
          <div className="flex gap-2 mb-3">
            {CHANNEL_OPTIONS.map(opt => (
              <button
                key={opt.value}
                onClick={() => setChannel(opt.value)}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                  channel === opt.value
                    ? 'bg-primary-100 text-primary-700 ring-2 ring-primary-500'
                    : 'bg-secondary-100 text-secondary-600 hover:bg-secondary-200'
                }`}
              >
                {opt.label}
              </button>
            ))}
          </div>

          {/* Search and filter */}
          <div className="flex gap-3">
            <div className="flex-1 relative">
              <svg className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-secondary-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
              <Input
                type="text"
                placeholder="Search recipients..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10"
              />
            </div>
            <select
              value={filterType}
              onChange={(e) => setFilterType(e.target.value as any)}
              className="px-3 py-2 border border-secondary-300 rounded-lg focus:ring-2 focus:ring-primary-500"
            >
              <option value="all">All</option>
              <option value="player">Players</option>
              <option value="guardian">Guardians</option>
            </select>
          </div>
        </div>

        {/* Select All */}
        <div className="px-4 py-2 border-b border-secondary-100 flex items-center justify-between">
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={filteredRecipients.length > 0 && selectedIds.size === filteredRecipients.length}
              onChange={toggleSelectAll}
              className="rounded border-secondary-300 text-primary-600 focus:ring-primary-500"
            />
            <span className="text-sm text-secondary-700">
              Select All ({filteredRecipients.length})
            </span>
          </label>
          <span className="text-sm text-primary-600 font-medium">
            {selectedIds.size} selected
          </span>
        </div>

        {/* Recipient list */}
        <div className="overflow-y-auto max-h-[400px]">
          {loading ? (
            <div className="flex justify-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
            </div>
          ) : filteredRecipients.length === 0 ? (
            <div className="text-center py-8">
              <p className="text-secondary-500 text-sm">No recipients available</p>
            </div>
          ) : (
            filteredRecipients.map(r => (
              <label
                key={r.id}
                className={`flex items-center gap-3 px-4 py-3 border-b border-secondary-100 cursor-pointer hover:bg-secondary-50 transition-colors ${
                  selectedIds.has(r.id) ? 'bg-primary-50' : ''
                }`}
              >
                <input
                  type="checkbox"
                  checked={selectedIds.has(r.id)}
                  onChange={() => toggleSelect(r.id)}
                  className="rounded border-secondary-300 text-primary-600 focus:ring-primary-500"
                />
                <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-semibold flex-shrink-0 ${
                  r.type === 'player' ? 'bg-blue-100 text-blue-700' : 'bg-purple-100 text-purple-700'
                }`}>
                  {r.name.charAt(0).toUpperCase()}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-secondary-900 truncate">{r.name}</p>
                  <p className="text-xs text-secondary-500 truncate">{getContactDisplay(r)}</p>
                </div>
                <span className={`px-2 py-0.5 text-xs rounded-full ${
                  r.type === 'player' ? 'bg-blue-50 text-blue-700' : 'bg-purple-50 text-purple-700'
                }`}>
                  {r.type === 'player' ? 'Player' : 'Guardian'}
                </span>
              </label>
            ))
          )}
        </div>
      </Card>

      {/* Right: Compose */}
      <Card>
        <div className="p-4 border-b border-secondary-200">
          <h3 className="font-semibold text-secondary-900">Compose Message</h3>
        </div>

        <div className="p-4 space-y-4">
          {/* Email subject */}
          {channel === 'email' && (
            <div>
              <Label htmlFor="bulkSubject">Subject</Label>
              <Input
                id="bulkSubject"
                type="text"
                value={subject}
                onChange={(e) => setSubject(e.target.value)}
                placeholder="Enter email subject..."
              />
            </div>
          )}

          {/* Message body */}
          <div>
            <Label htmlFor="bulkBody">Message</Label>
            <textarea
              id="bulkBody"
              value={body}
              onChange={(e) => setBody(e.target.value)}
              placeholder="Type your message..."
              rows={8}
              className="w-full px-3 py-2 border border-secondary-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent resize-none"
            />
            {channel === 'sms' && body.length > 0 && (
              <p className={`text-xs mt-1 ${body.length > SMS_SEGMENT_LENGTH ? 'text-warning-600' : 'text-secondary-400'}`}>
                {body.length}/{SMS_SEGMENT_LENGTH} chars
                {smsSegments > 1 && ` (${smsSegments} segments)`}
              </p>
            )}
          </div>

          {/* Summary */}
          <div className="p-3 bg-secondary-50 rounded-lg space-y-1">
            <p className="text-sm font-medium text-secondary-900">Summary</p>
            <p className="text-sm text-secondary-600">
              Channel: <span className="font-medium capitalize">{channel}</span>
            </p>
            <p className="text-sm text-secondary-600">
              Recipients: <span className="font-medium">{selectedIds.size}</span>
            </p>
            <p className="text-sm text-secondary-600">
              Credits Required: <span className="font-medium">{selectedIds.size}</span>
            </p>
            <p className={`text-sm ${insufficientCredits ? 'text-red-600 font-medium' : 'text-secondary-600'}`}>
              Available Credits: <span className="font-medium">{availableCredits.toLocaleString()}</span>
            </p>
            {insufficientCredits && selectedIds.size > 0 && (
              <p className="text-sm text-red-600 font-medium">
                Not enough credits to send to {selectedIds.size} recipient(s).
              </p>
            )}
            {body.trim() && (
              <p className="text-sm text-secondary-600 truncate">
                Preview: <span className="italic">{body.trim().substring(0, 80)}{body.trim().length > 80 ? '...' : ''}</span>
              </p>
            )}
          </div>

          {/* Send */}
          <div className="flex justify-end">
            <Button
              onClick={() => setShowConfirm(true)}
              disabled={sending || selectedIds.size === 0 || !body.trim() || (channel === 'email' && !subject.trim()) || insufficientCredits}
            >
              {sending ? (
                <>
                  <svg className="animate-spin -ml-1 mr-2 h-4 w-4" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Sending...
                </>
              ) : (
                <>
                  <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" />
                  </svg>
                  Send to {selectedIds.size} Recipient{selectedIds.size !== 1 ? 's' : ''}
                </>
              )}
            </Button>
          </div>
        </div>
      </Card>

      {/* Confirmation Dialog */}
      {showConfirm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl shadow-xl p-6 max-w-md mx-4">
            <h3 className="text-lg font-semibold text-secondary-900 mb-2">Confirm Bulk Message</h3>
            <p className="text-secondary-600 mb-4">
              You are about to send a <span className="font-medium capitalize">{channel}</span> message
              to <span className="font-medium">{selectedIds.size}</span> recipient{selectedIds.size !== 1 ? 's' : ''}.
            </p>
            {body.trim() && (
              <div className="p-3 bg-secondary-50 rounded-lg mb-4">
                <p className="text-sm text-secondary-700 whitespace-pre-wrap">
                  {body.trim().substring(0, 200)}{body.trim().length > 200 ? '...' : ''}
                </p>
              </div>
            )}
            <div className="flex justify-end gap-3">
              <Button variant="outline" onClick={() => setShowConfirm(false)}>
                Cancel
              </Button>
              <Button onClick={handleSend} disabled={sending}>
                {sending ? 'Sending...' : 'Confirm Send'}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default BulkMessage;
